/**
 * What the app has learned about this player.
 *
 * ## The anti-trap requirement
 *
 * Personalisation here is allowed to *lean*, never to *narrow*. A preference model that
 * gets good at predicting what someone will play is, in this product, a model that stops
 * showing them anything new — and the entire point is to be shown something you would not
 * have thought of. `PreferenceHints` is documented in `@hc/shared` as hints that "ride
 * along with the branch request so the model can lean toward the kinds of movement this
 * player actually takes, but nothing here is allowed to filter suggestions out".
 *
 * Three things enforce that, and they are deliberately in three different places:
 *
 *  1. **The server caps the weighting.** However lopsided `takenKindCounts` becomes, the
 *     prompt cannot turn into "only ever suggest secondary dominants".
 *  2. **This file exposes the raw hints.** `get()` returns exactly what is sent, so the
 *     settings screen can show a player what the app thinks of them. A personalisation
 *     model nobody can inspect is one nobody can disagree with.
 *  3. **`reset()` is one call.** Undoing the learning must be as easy as doing it.
 *
 * Counts are also kept deliberately coarse — a count per movement kind, an EMA, a handful
 * of keys — because a richer model would be a better predictor and a worse companion.
 */

import {
  createEmptyPreferenceHints,
  updateEma,
  ADVENTUROUSNESS_EMA_ALPHA,
  type BranchSuggestion,
  type MovementKind,
  type PitchClass,
  type PreferenceHints,
} from '@hc/shared';
import {
  loadPreferenceHints,
  loadPreferenceTallies,
  resetPreferences,
  savePreferenceHints,
  savePreferenceTallies,
  type PreferenceTallies,
} from '../sketchbook/queries.js';

/** How many tonics `commonKeys` reports. Enough to be a hint, too few to be a rut. */
export const COMMON_KEYS_LIMIT = 5;

/** Liked transitions are sent with every request, so the list is bounded. */
export const LIKED_TRANSITIONS_LIMIT = 40;

/** Smoothing for the explanation tap rate. Same shape as the adventurousness EMA. */
export const EXPLANATION_RATE_ALPHA = 0.15;

/** Writes are debounced: a burst of chords should not be a burst of transactions. */
export const PREFERENCE_SAVE_DEBOUNCE_MS = 1_000;

// ---------------------------------------------------------------------------
// Pure updates — the whole model, testable without storage
// ---------------------------------------------------------------------------

/**
 * Fold one chosen branch into the hints.
 *
 * Only the *taken* kind is counted here. Ignored kinds are session-scoped and travel on
 * the request directly, because "passed over once" is much weaker evidence than "played",
 * and accumulating it across sessions would quietly build the filter this file exists to
 * avoid.
 */
export function withTakenSuggestion(
  hints: PreferenceHints,
  taken: BranchSuggestion,
): PreferenceHints {
  return {
    ...hints,
    takenKindCounts: {
      ...hints.takenKindCounts,
      [taken.movementKind]: (hints.takenKindCounts[taken.movementKind] ?? 0) + 1,
    },
    adventurousnessEma: round4(
      updateEma(hints.adventurousnessEma, taken.adventurousness, ADVENTUROUSNESS_EMA_ALPHA),
    ),
  };
}

/** Fold an explicit adventurousness setting into the moving average. */
export function withAdventurousness(hints: PreferenceHints, value: number): PreferenceHints {
  return {
    ...hints,
    adventurousnessEma: round4(
      updateEma(hints.adventurousnessEma, clamp01(value), ADVENTUROUSNESS_EMA_ALPHA),
    ),
  };
}

export function withExplanationObservation(
  hints: PreferenceHints,
  opened: boolean,
): PreferenceHints {
  return {
    ...hints,
    explanationTapRate: round4(
      updateEma(hints.explanationTapRate, opened ? 1 : 0, EXPLANATION_RATE_ALPHA),
    ),
  };
}

function sameTransition(
  a: { fromPcs: PitchClass[]; toPcs: PitchClass[] },
  b: { fromPcs: readonly PitchClass[]; toPcs: readonly PitchClass[] },
): boolean {
  return (
    a.fromPcs.join(',') === [...b.fromPcs].sort((x, y) => x - y).join(',') &&
    a.toPcs.join(',') === [...b.toPcs].sort((x, y) => x - y).join(',')
  );
}

export function withLikedTransition(
  hints: PreferenceHints,
  fromPcs: readonly PitchClass[],
  toPcs: readonly PitchClass[],
): PreferenceHints {
  const entry = {
    fromPcs: [...fromPcs].sort((a, b) => a - b),
    toPcs: [...toPcs].sort((a, b) => a - b),
  };
  if (hints.likedTransitions.some((t) => sameTransition(t, entry))) return hints;
  return {
    ...hints,
    likedTransitions: [...hints.likedTransitions, entry].slice(-LIKED_TRANSITIONS_LIMIT),
  };
}

export function withoutLikedTransition(
  hints: PreferenceHints,
  fromPcs: readonly PitchClass[],
  toPcs: readonly PitchClass[],
): PreferenceHints {
  return {
    ...hints,
    likedTransitions: hints.likedTransitions.filter((t) => !sameTransition(t, { fromPcs, toPcs })),
  };
}

/** Most-played tonics first, capped. Ties break toward the lower pitch class. */
export function orderCommonKeys(keyCounts: Record<string, number>): PitchClass[] {
  return Object.entries(keyCounts)
    .map(([pc, count]) => ({ pc: Number(pc) as PitchClass, count }))
    .filter((entry) => entry.count > 0 && entry.pc >= 0 && entry.pc <= 11)
    .sort((a, b) => b.count - a.count || a.pc - b.pc)
    .slice(0, COMMON_KEYS_LIMIT)
    .map((entry) => entry.pc);
}

/** The movement kind this player reaches for most, or `null` when there is no evidence. */
export function mostTakenKind(hints: PreferenceHints): MovementKind | null {
  let best: MovementKind | null = null;
  let bestCount = 0;
  for (const [kind, count] of Object.entries(hints.takenKindCounts)) {
    if (count > bestCount) {
      best = kind as MovementKind;
      bestCount = count;
    }
  }
  return bestCount > 0 ? best : null;
}

function clamp01(value: number): number {
  return value < 0 ? 0 : value > 1 ? 1 : value;
}

function round4(value: number): number {
  return Math.round(value * 1e4) / 1e4;
}

// ---------------------------------------------------------------------------
// The controller
// ---------------------------------------------------------------------------

export interface PreferencesController {
  /**
   * The hints exactly as they are sent to the server. The settings screen renders this;
   * see the anti-trap note at the top of the file.
   */
  get(): PreferenceHints;
  /** The private counters behind the hints, for the same screen. */
  tallies(): PreferenceTallies;
  /** Resolves once the stored hints have been read. Safe to call repeatedly. */
  ready(): Promise<PreferenceHints>;
  subscribe(listener: (hints: PreferenceHints) => void): () => void;

  recordTaken(taken: BranchSuggestion): void;
  recordAdventurousness(value: number): void;
  recordKey(tonicPc: PitchClass): void;
  /** An explanation was available and the player did or did not open it. */
  recordExplanationOffer(opened: boolean): void;
  likeTransition(fromPcs: readonly PitchClass[], toPcs: readonly PitchClass[]): void;
  unlikeTransition(fromPcs: readonly PitchClass[], toPcs: readonly PitchClass[]): void;

  /** Forget everything. One call, by design. */
  reset(): Promise<PreferenceHints>;
  flush(): Promise<void>;
  dispose(): void;
}

export interface CreatePreferencesOptions {
  debounceMs?: number;
  /** Injected in tests. */
  storage?: {
    load: () => Promise<PreferenceHints>;
    save: (hints: PreferenceHints) => Promise<void>;
    loadTallies: () => Promise<PreferenceTallies>;
    saveTallies: (tallies: PreferenceTallies) => Promise<void>;
    reset: () => Promise<PreferenceHints>;
  };
  onError?: (error: unknown) => void;
}

const defaultStorage: NonNullable<CreatePreferencesOptions['storage']> = {
  load: loadPreferenceHints,
  save: savePreferenceHints,
  loadTallies: loadPreferenceTallies,
  saveTallies: savePreferenceTallies,
  reset: resetPreferences,
};

export function createPreferencesController(
  options: CreatePreferencesOptions = {},
): PreferencesController {
  const storage = options.storage ?? defaultStorage;
  const debounceMs = options.debounceMs ?? PREFERENCE_SAVE_DEBOUNCE_MS;
  const onError = options.onError ?? (() => {});

  let hints: PreferenceHints = createEmptyPreferenceHints();
  let tallies: PreferenceTallies = {
    keyCounts: {},
    explanationsOffered: 0,
    explanationsOpened: 0,
    sessionCount: 0,
  };
  const listeners = new Set<(hints: PreferenceHints) => void>();
  let timer: ReturnType<typeof setTimeout> | null = null;
  let inFlight: Promise<void> = Promise.resolve();
  let loaded: Promise<PreferenceHints> | null = null;
  let disposed = false;

  function persist(): void {
    if (disposed) return;
    if (timer !== null) clearTimeout(timer);
    timer = setTimeout(() => {
      timer = null;
      const snapshotHints = hints;
      const snapshotTallies = tallies;
      inFlight = inFlight.then(async () => {
        try {
          await storage.save(snapshotHints);
          await storage.saveTallies(snapshotTallies);
        } catch (error) {
          onError(error);
        }
      });
    }, debounceMs);
  }

  function update(next: PreferenceHints): void {
    if (next === hints) return;
    hints = next;
    for (const listener of listeners) listener(hints);
    persist();
  }

  return {
    get: () => hints,
    tallies: () => tallies,
    ready() {
      if (!loaded) {
        loaded = (async () => {
          try {
            const [storedHints, storedTallies] = await Promise.all([
              storage.load(),
              storage.loadTallies(),
            ]);
            tallies = storedTallies;
            hints = { ...storedHints, commonKeys: orderCommonKeys(storedTallies.keyCounts) };
            for (const listener of listeners) listener(hints);
          } catch (error) {
            onError(error);
          }
          return hints;
        })();
      }
      return loaded;
    },
    subscribe(listener) {
      listeners.add(listener);
      return () => listeners.delete(listener);
    },
    recordTaken(taken) {
      update(withTakenSuggestion(hints, taken));
    },
    recordAdventurousness(value) {
      update(withAdventurousness(hints, value));
    },
    recordKey(tonicPc) {
      const keyCounts = { ...tallies.keyCounts, [tonicPc]: (tallies.keyCounts[tonicPc] ?? 0) + 1 };
      tallies = { ...tallies, keyCounts };
      update({ ...hints, commonKeys: orderCommonKeys(keyCounts) });
    },
    recordExplanationOffer(opened) {
      tallies = {
        ...tallies,
        explanationsOffered: tallies.explanationsOffered + 1,
        explanationsOpened: tallies.explanationsOpened + (opened ? 1 : 0),
      };
      update(withExplanationObservation(hints, opened));
    },
    likeTransition(fromPcs, toPcs) {
      update(withLikedTransition(hints, fromPcs, toPcs));
    },
    unlikeTransition(fromPcs, toPcs) {
      update(withoutLikedTransition(hints, fromPcs, toPcs));
    },
    async reset() {
      if (timer !== null) {
        clearTimeout(timer);
        timer = null;
      }
      hints = await storage.reset();
      tallies = { keyCounts: {}, explanationsOffered: 0, explanationsOpened: 0, sessionCount: 0 };
      for (const listener of listeners) listener(hints);
      return hints;
    },
    async flush() {
      if (timer !== null) {
        clearTimeout(timer);
        timer = null;
        const snapshotHints = hints;
        const snapshotTallies = tallies;
        inFlight = inFlight.then(async () => {
          try {
            await storage.save(snapshotHints);
            await storage.saveTallies(snapshotTallies);
          } catch (error) {
            onError(error);
          }
        });
      }
      await inFlight;
    },
    dispose() {
      disposed = true;
      if (timer !== null) clearTimeout(timer);
      timer = null;
      listeners.clear();
    },
  };
}
